import React, { useCallback, useRef } from 'react';
import { List as DxList } from 'devextreme-react/list';
import type { Expense } from '#/graphql/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import List from '#/common/components/List';
import { getExpenseList } from '#/graphql/queries';
import { gql } from '@apollo/client';
import columns from '../ExpenseDataGrid/columns';
import { Filter } from '#/common/components/DataGrid';
import { withChip } from '#/common/utils/wrappers';
import { statusItems } from '../../utils/constants';
import { expenseStatusColors } from '../ExpenseDataGrid/columns';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';
import useToast from '#/common/hooks/useToast';
import useExpenseDelete from '../../hooks/useExpenseDelete';

export interface ExpenseListProps {
  permanentFilters?: Filter[];
}

const getExpenseListQuery = gql(getExpenseList);

const ExpenseList: React.FC<ExpenseListProps> = ({ permanentFilters }) => {
  const list = useRef<DxList | null>(null);
  const { confirm } = useToast();
  const { deleteExpense } = useExpenseDelete();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workOrderId =
    searchParams.get('workOrderId') ||
    getPermanentFilterValue('workOrderId', permanentFilters) ||
    undefined;
  const tenantId =
    searchParams.get('tenantId') ||
    getPermanentFilterValue('tenantId', permanentFilters) ||
    undefined;

  const params = [];

  if (workOrderId) params.push(`workOrderId=${workOrderId}`);
  if (tenantId) params.push(`tenantId=${tenantId}`);

  const openExpense = useCallback(
    (expense: Expense) => {
      navigate(`/expenses/${expense.id}`);
    },
    [navigate],
  );

  const deleteItems = useCallback(
    async (items?: Expense[], clearSelectedItems?: () => void) => {
      if (!items || items.length === 0) return;

      // Identify which ones can be deleted
      const deletable = items.filter((exp) => exp.status === 'draft');
      const nonDeletable = items.filter((exp) => exp.status !== 'draft');

      let confirmMessage = '';

      if (nonDeletable.length > 0) {
        confirmMessage = `${deletable.length} of ${items.length} selected expenses will be deleted.\nOnly expenses with "Draft" status can be deleted.\nContinue?`;
      } else {
        confirmMessage = `Are you sure you want to delete ${deletable.length} expense${
          deletable.length > 1 ? 's' : ''
        }?`;
      }

      const sure = await confirm('error', confirmMessage);
      if (!sure) return;

      if (deletable.length === 0) return;

      const ids = deletable.map((x) => x.id).join(',');
      await deleteExpense(ids, workOrderId);
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteExpense, confirm, workOrderId],
  );

  return (
    <List<Expense>
      setRef={(ref) => (list.current = ref)}
      titleKey="title"
      onClick={openExpense}
      getItemsQuery={getExpenseListQuery}
      subtitleKey="displayNumber"
      customChip={(item: Expense) => {
        const statusLabel = statusItems.find((s) => s.key === item.status)?.label || '';
        const statusColors = item.status ? expenseStatusColors[item.status] : undefined;
        return item.status ? withChip(statusLabel, 'normal', statusColors) : null;
      }}
      permanentFilters={permanentFilters}
      bodyFields={[
        {
          key: (item: Expense) => item.user?.name || item.createdBy || '-',
          label: 'Created By',
          labelKey: 'created-by',
        },
        {
          key: (item: Expense) => `$${item.amount || '-'}`,
          label: 'Amount',
        },
        {
          key: 'workOrder.displayNumber',
          label: 'Work Order',
        },
      ]}
      columnList={columns}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItems}
    />
  );
};

export default React.memo(ExpenseList);
